<template>
  <div class="chat-sidebar border-right">
    <div class="d-flex align-items-center justify-content-between p-3">
      <h2 class="mb-0">Chats</h2>
      <b-btn
        v-b-modal.create-chat
        v-b-tooltip.hover="'Create chat'"
        variant="light"
        pill
      >
        <i class="icon-note" />
      </b-btn>
    </div>

    <div>
      <a
        v-for="group in sortedGroups"
        :key="group.id"
        :class="{ active: selectedId === group.id }"
        class="chat-group"
        @click="selectGroup(group.id)"
      >
        <h5 class="chat-name mb-0">{{ group.name || userList(group.id) }}</h5>
        <template v-if="group.last_message">
          <div class="last-message">
            {{ lastMessage(group) }}
          </div>
          <div :key="`${group.id}-${timeKey}`" class="small text-muted">
            {{ timeAgo(group.last_message.created_at) }}
          </div>
        </template>
        <div v-if="isUnread(group)" class="unread-indicator"></div>
      </a>

      <div v-if="!groups.length" class="text-center text-muted">
        <div class="mt-5">
          <i class="icon-bubbles font-xl" />
        </div>
        <div>You have no chats yet</div>
      </div>
    </div>

    <CreateChat @create="createChat" />
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import API from "@/api";

import CreateChat from "./CreateChat";

export default {
  components: {
    CreateChat
  },

  data: () => ({
    timeKey: 0,
    timeIid: null
  }),

  computed: {
    ...mapGetters("contacts", ["getNickname"]),
    ...mapGetters("chat", ["userList"]),
    ...mapState("chat", ["groups", "selectedId"]),
    ...mapState(["user"]),

    sortedGroups() {
      return this.groups.slice().sort((a, b) => {
        const aTime = a.last_message ? a.last_message.created_at : a.created_at;
        const bTime = b.last_message ? b.last_message.created_at : b.created_at;
        return moment(bTime).diff(aTime);
      });
    }
  },

  mounted() {
    this.timeIid = setInterval(() => {
      this.timeKey = Date.now();
    }, 10000);

    this.$once("hook:beforeDestroy", () => {
      clearInterval(this.timeIid);
    });
  },

  methods: {
    ...mapActions("chat", ["selectGroup"]),

    async createChat(ids) {
      const existing = this.groups.find(group => {
        const ids1 = group.users.map(u => u.id);
        const ids2 = ids.concat(this.user.id);
        const allMatch = ids1.every(id => ids2.includes(id));
        return ids1.length === ids2.length && allMatch;
      });

      if (existing) {
        this.selectGroup(existing.id);
        return;
      }

      const res = await API.createChatGroup(ids);
      if (!res.success) return;

      this.selectGroup(res.results.group_id);
    },

    isUnread(group) {
      if (!group.last_message) return false;
      const last_message = new Date(group.last_message.created_at);
      const last_read = new Date(group.last_read_at);
      return last_message > last_read;
    },

    lastMessage(group) {
      if (!group.last_message) return "";
      const { attachment, message, user } = group.last_message;
      const isMe = user.id === this.user.id;
      const username = isMe ? "You" : this.getNickname(user);
      const preview = attachment
        ? attachment.filename
        : message || "message deleted";
      return `${username}: ${preview}`;
    },

    timeAgo(time) {
      return moment(time).fromNow();
    }
  }
};
</script>

<style lang="scss">
.chat-sidebar {
  width: 20rem;
  background: var(--white);

  .chat-group {
    position: relative;
    color: unset;
    text-decoration: none;
    display: block;
    padding: 1em 2em;
    margin: 0.5em;
    border-radius: 0.5em;
    cursor: pointer;

    &:hover,
    &.active {
      background: var(--light);
    }

    .chat-name,
    .last-message {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .unread-indicator {
      position: absolute;
      right: 2em;
      top: 50%;
      width: 8px;
      height: 8px;
      background: var(--primary);
      margin: -4px;
      border-radius: 4px;
    }
  }
}
</style>
